import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

export default function DocentesPorMateriaTabla({ nombreMateria, devuelvo }) {
  const [docentesPorMateria, setDocentesPorMateria] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const maxHorasPorTipo = {
    "Policia Actividad": 12,
    "Policia Retirado": 10,
    "Personal Civil": 36,
  };

  const nombreMeses = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  useEffect(() => {
    if (!nombreMateria || nombreMateria.trim().length < 4) return;

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    const fetchDocentes = async () => {
      try {
        const response = await axios.post(
          `https://wwwa.isepsantafe.net.ar/expedientes/docentes/getDocentesPorMateria/${encodeURIComponent(nombreMateria)}`,
          {},
          { signal: controller.signal }
        );
        setDocentesPorMateria(response.data.docentesPorMateria || {});
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error("Error al obtener docentes por materia:", err);
          setError("No se han encontrado docentes con la incumbencia seleccionada");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDocentes();

    return () => controller.abort();
  }, [nombreMateria]);

  if (!nombreMateria) return <p>Ingrese el nombre de la materia.</p>;
  if (loading) return <p>Cargando docentes...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!docentesPorMateria || Object.keys(docentesPorMateria).length === 0)
    return <p>No se han encontrado docentes con la incumbencia seleccionada</p>;

  const materiasConDocentesActivos = Object.entries(docentesPorMateria).filter(
    ([, data]) => data.docentes.some(d => d.DocenteIncumbencia?.estado?.toLowerCase() === "activo")
  );

  if (materiasConDocentesActivos.length === 0) {
    return <p>No se han encontrado docentes con la incumbencia seleccionada</p>;
  }

  const generarMesesHorasLibres = (docente) => {
    const asignaciones = docente.asignacionMaterias || [];
    const fechaActual = new Date();
    const maxHoras = maxHorasPorTipo[docente.docenteTipo] || 12;

    return Array.from({ length: 12 }).map((_, i) => {
      const mesFecha = new Date(fechaActual.getFullYear(), fechaActual.getMonth() + i, 1);
      const mes = mesFecha.getMonth();
      const anio = mesFecha.getFullYear();
      const nombreMes = nombreMeses[mes] + " " + anio;

      let horasAsignadas = 0;
      asignaciones.forEach((a) => {
        if (a.estado?.toLowerCase() !== "activo") return;
        const inicio = new Date(a.fechaInicio);
        const fin = new Date(a.fechaFinalizacion);
        const primerDiaMes = new Date(anio, mes, 1);
        const ultimoDiaMes = new Date(anio, mes + 1, 0);
        if (fin >= primerDiaMes && inicio <= ultimoDiaMes) {
          horasAsignadas += Number(a.horasAsignadas || 0);
        }
      });

      const horasLibres = Math.max(maxHoras - horasAsignadas, 0);

      // Determinar color semáforo
      let color = "green";
      if (horasLibres / maxHoras >= 0.7) color = "green";
      else if (horasLibres / maxHoras >= 0.3) color = "yellow";
      else color = "red";

      return { nombre: nombreMes, horasLibres, color };
    });
  };

  const exportarExcelMateria = (materia, docentes) => {
    const dataExcel = docentes.map((doc) => {
      if (doc.DocenteIncumbencia?.estado?.toLowerCase() !== "activo") return null;
      const mesesHoras = generarMesesHorasLibres(doc);
      const fila = {
        Apellido: doc.docenteApellido,
        Nombres: doc.docenteNombres,
        DNI: doc.docenteDni,
        Tipo: doc.docenteTipo,
        Email: doc.docenteEmail,
        Teléfono: doc.docenteTelefono,
      };
      mesesHoras.forEach((m) => {
        fila[m.nombre] = m.horasLibres;
      });
      return fila;
    }).filter(Boolean);

    const ws = XLSX.utils.json_to_sheet(dataExcel);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Docentes");
    XLSX.writeFile(wb, `${materia}.xlsx`);
  };

  const exportarExcelIndividual = (doc) => {
    const mesesHoras = generarMesesHorasLibres(doc);
    const fila = {
      Apellido: doc.docenteApellido,
      Nombres: doc.docenteNombres,
      DNI: doc.docenteDni,
      Tipo: doc.docenteTipo,
      Email: doc.docenteEmail,
      Teléfono: doc.docenteTelefono,
    };
    mesesHoras.forEach((m) => {
      fila[m.nombre] = m.horasLibres;
    });

    const ws = XLSX.utils.json_to_sheet([fila]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Docente");
    XLSX.writeFile(wb, `${doc.docenteApellido}_${doc.docenteDni}.xlsx`);
  };

  const exportarExcelTodasMaterias = () => {
    const wb = XLSX.utils.book_new();

    materiasConDocentesActivos.forEach(([materia, data]) => {
      const docentesActivos = data.docentes.filter(
        (d) => d.DocenteIncumbencia?.estado?.toLowerCase() === "activo"
      );
      const dataExcel = docentesActivos.map((doc) => {
        const mesesHoras = generarMesesHorasLibres(doc);
        const fila = {
          Apellido: doc.docenteApellido,
          Nombres: doc.docenteNombres,
          DNI: doc.docenteDni,
          Tipo: doc.docenteTipo,
          Email: doc.docenteEmail,
          Teléfono: doc.docenteTelefono,
        };
        mesesHoras.forEach((m) => {
          fila[m.nombre] = m.horasLibres;
        });
        return fila;
      });
      const ws = XLSX.utils.json_to_sheet(dataExcel);
      XLSX.utils.book_append_sheet(wb, ws, materia.substring(0, 30));
    });

    XLSX.writeFile(wb, `Docentes_TodasMaterias.xlsx`);
  };

  const mesesColumnas = Array.from({ length: 12 }).map((_, i) => {
    const fecha = new Date();
    fecha.setMonth(fecha.getMonth() + i);
    return nombreMeses[fecha.getMonth()] + " " + fecha.getFullYear();
  });

  return (
    <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <div style={{ maxWidth: "100%", overflowX: "auto" }}>
        <button
          style={{ marginBottom: "15px", padding: "6px 12px" }}
          onClick={exportarExcelTodasMaterias}
        >
          Exportar Todas las Materias
        </button>
        {materiasConDocentesActivos.map(([materia, data]) => {
          const docentesActivos = data.docentes.filter(
            (d) => d.DocenteIncumbencia?.estado?.toLowerCase() === "activo"
          );

          return (
            <div key={data.id} style={{ marginBottom: "20px" }}>
              <h3 style={{ marginBottom: "8px" }}>
                {materia} ({data.descripcion.trim()})
                <button
                  style={{ marginLeft: "10px", padding: "4px 8px" }}
                  onClick={() => exportarExcelMateria(materia, docentesActivos)}
                >
                  Exportar Excel
                </button>
              </h3>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "15px",
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  overflow: "hidden",
                }}
              >
                <thead>
                  <tr style={{ background: "#f1f1f1" }}>
                    <th style={{ padding: "10px", textAlign: "left" }}>Apellido</th>
                    <th style={{ padding: "10px", textAlign: "left" }}>Nombres</th>
                    <th style={{ padding: "10px", textAlign: "center" }}>DNI</th>
                    <th style={{ padding: "10px", textAlign: "center" }}>Tipo</th>
                    <th style={{ padding: "10px", textAlign: "center" }}>Email</th>
                    <th style={{ padding: "10px", textAlign: "center" }}>Teléfono</th>
                    {mesesColumnas.map((m) => (
                      <th key={m} style={{ padding: "10px", textAlign: "center" }}>
                        {m}
                      </th>
                    ))}
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {docentesActivos.map((doc) => {
                    const mesesHoras = generarMesesHorasLibres(doc);
                    return (
                      <tr
                        key={doc.docenteDni}
                        onClick={() => devuelvo(doc)}
                        style={{ cursor: "pointer", transition: "background 0.2s" }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#f8f9fa")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor = "white")
                        }
                      >
                        <td style={{ padding: "10px 12px" }}>{doc.docenteApellido}</td>
                        <td style={{ padding: "10px 12px" }}>{doc.docenteNombres}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>{doc.docenteDni}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>{doc.docenteTipo}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>{doc.docenteEmail}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>{doc.docenteTelefono}</td>
                        {mesesHoras.map((m) => (
                          <td
                            key={m.nombre}
                            style={{
                              padding: "10px",
                              textAlign: "center",
                              backgroundColor: m.color,
                              color: m.color === "yellow" ? "#000" : "#fff",
                              fontWeight: "bold",
                            }}
                          >
                            {m.horasLibres}
                          </td>
                        ))}
                        <td>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              exportarExcelIndividual(doc);
                            }}
                          >
                            Exportar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
}
